import { permissionsMappings } from '../../api/auth/token-decoder';

export enum PermissionGroupKey {
  'EditPatients' = 1,
  'TransmitData' = 2,
  'ManagePatients' = 3,
  'EditExternalIds' = 4,
  'MergePatients' = 5,
  'DeleteData' = 6,
  'ManageTemplates' = 7,
  'ManageResources' = 8,
  'PlanOccupancy' = 9,
  'ConfigureUI' = 10,
  'ConfigureSystem' = 11,
  'ManageUsers' = 12,
  'AuditLogs' = 13,
  'AdvancedConfigurations' = 14,
}

export const permissionGroups = [
  {
    key: PermissionGroupKey.EditPatients,
    name: 'edit_patients',
    permissions: [
      permissionsMappings.importMeasurements,
      permissionsMappings.dataBlockManagement,
      permissionsMappings.createOrEditDocuments,
    ],
    color: '#F3F5FA',
  },
  {
    key: PermissionGroupKey.TransmitData,
    name: 'transmit_data',
    permissions: [
      permissionsMappings.hl7,
      permissionsMappings.gdt,
      permissionsMappings.sendDocumentByEmail,
    ],
    access: PermissionGroupKey.EditPatients,
    color: '#E3F1FB',
  },
  {
    key: PermissionGroupKey.ManagePatients,
    name: 'manage_patients',
    permissions: [
      permissionsMappings.deleteCases,
      permissionsMappings.reopenCase,
      permissionsMappings.deleteDocuments,
      permissionsMappings.patientDeleteAndRestore,
      permissionsMappings.movePatientsToAnotherSubtenant,
      permissionsMappings.deleteDataBlocks,
      permissionsMappings.deleteVisits,
    ],
    access: PermissionGroupKey.EditPatients,
    color: '#FEF7F7',
  },
  {
    key: PermissionGroupKey.EditExternalIds,
    name: 'edit_external_ids',
    permissions: [
      permissionsMappings.editExternalPatientNumber,
      permissionsMappings.editExternalVisitNumber,
    ],
    access: PermissionGroupKey.EditPatients,
    color: '#EBF9F9',
  },
  {
    key: PermissionGroupKey.MergePatients,
    name: 'merge_patients',
    permissions: [permissionsMappings.mergePatients],
    access: PermissionGroupKey.EditPatients,
    color: '#F0F0F0',
  },
  {
    key: PermissionGroupKey.DeleteData,
    name: 'delete_data',
    permissions: [permissionsMappings.patientAnonymizeAndDeletePermanently],
    access: PermissionGroupKey.EditPatients,
    color: '#FEF4E5',
  },
  {
    key: PermissionGroupKey.ManageTemplates,
    name: 'manage_templates',
    permissions: [
      permissionsMappings.documentTemplateManagement,
      permissionsMappings.editTenantDashboardUIConfiguration,
      permissionsMappings.editTenantPatientOverviewUIConfiguration,
      permissionsMappings.manageSharedCalendarConfigurations,
      permissionsMappings.calendarSettingsManagement,
      permissionsMappings.manageSharedPatientListConfigurations,
      permissionsMappings.inputTemplateManagement,
      permissionsMappings.statisticsDesigner,
    ],
    color: '#F7F5FF',
  },
  {
    key: PermissionGroupKey.ManageResources,
    name: 'manage_resources',
    permissions: [
      permissionsMappings.dictionariesManagement,
      permissionsMappings.manageResources,
      permissionsMappings.doctorManagement,
    ],
    color: '#F8FFEF',
  },
  {
    key: PermissionGroupKey.PlanOccupancy,
    name: 'plan_occupancy',
    permissions: [
      permissionsMappings.manageAbsences,
      permissionsMappings.statisticsReport,
    ],
    color: '#FCFEEC',
  },
  {
    key: PermissionGroupKey.ConfigureUI,
    name: 'configure_ui',
    permissions: [
      permissionsMappings.configureMeasurementUI,
      permissionsMappings.deactivateDatablocks,
    ],
    color: '#FFFDE7',
  },
  {
    key: PermissionGroupKey.ConfigureSystem,
    name: 'configure_system',
    permissions: [
      permissionsMappings.tenantSettingsChange,
      permissionsMappings.createSubtenant,
      permissionsMappings.deleteSubtenants,
      permissionsMappings.passwordValidationSettings,
      permissionsMappings.hl7Management,
      permissionsMappings.hl7Transporter,
      permissionsMappings.gdtManagement,
      permissionsMappings.gdtTransporter,
      permissionsMappings.editMeasurementMappings,
    ],
    color: '#FBF7F9',
  },
  {
    key: PermissionGroupKey.ManageUsers,
    name: 'manage_users',
    permissions: [
      permissionsMappings.userManagement,
      permissionsMappings.roleManagement,
      permissionsMappings.resetPassword,
    ],
    color: '#F7F5FF',
  },
  {
    key: PermissionGroupKey.AuditLogs,
    name: 'audit_logs',
    permissions: [permissionsMappings.auditLog],
    color: '#F5FFFA',
  },
  {
    key: PermissionGroupKey.AdvancedConfigurations,
    name: 'advanced_configurations',
    permissions: [
      permissionsMappings.reImportAllMeasurements,
      permissionsMappings.generateTestData,
      permissionsMappings.accessToAllSubtenants,
      permissionsMappings.mergeFieldManagement,
    ],
    roleName: 'TenantAdministrator',
    color: '#EBF9F9',
  },
];

export function getPermissionsByGroupKeys(
  groupKeys: PermissionGroupKey[],
): string[] {
  let permissions: string[] = [];
  for (const groupKey of groupKeys) {
    const group = permissionGroups.find((x) => x.key === groupKey);
    if (!group || (!!group.access && !groupKeys.includes(group.access))) {
      continue;
    }
    permissions.push(...group.permissions);
  }

  return permissions;
}

export function getGroupKeysByPermissions(
  permissions: string[],
): PermissionGroupKey[] {
  let groupKeys: PermissionGroupKey[] = [];
  for (const group of permissionGroups) {
    if (
      group.permissions.every((groupPermission) =>
        permissions.includes(groupPermission),
      )
    ) {
      groupKeys.push(group.key);
    }
  }
  return groupKeys;
}

export function getGroupsByPermissions(permissions: string[]) {
  let groups = [];
  for (const group of permissionGroups) {
    if (
      group.permissions.every((groupPermission) =>
        permissions.includes(groupPermission),
      )
    ) {
      groups.push(group);
    }
  }
  return groups;
}
